
export interface EventSession {
    sessionId: string;
    expiresAt: number;
};

/*
*   This typescript is based on the createActionGroup API in NGRX
*   so that we can enforce the structure of the event data
*   and group events based on feature slice
*   without having to define a type for each event
**/

/**
 * Type alias for event properties.
 */
type EventProps<P> = P;

/**
 * Interface representing an event configuration object.
 * Keys are event names and values are event properties.
 */
export interface EventConfig {
    [eventName: string]: EventProps<any>;
}

/**
 * Type for holding the event creator function we will use in our component
 */
export type EventCreator<P> = (props: P) => { type: string; props: P };

/**
 * Transforms an EventConfig into a collection of event creator functions.
 */
type EventCreators<T extends EventConfig> = {
    [K in keyof T]: EventCreator<T[K]>;
};


/**
 * Helper function to define event properties.
 */
export const eventProps = <P>() => ({} as EventProps<P>);

/**
 * Create a collection of event creator functions based on a feature slice, where each event has structured eventProps.
 */
export function createEventGroup<T extends EventConfig>(config: { feature: string; events: T }): EventCreators<T> {
    const eventCreators: Partial<EventCreators<T>> = {};

    for (const event in config.events) {
        if (Object.prototype.hasOwnProperty.call(config.events, event)) {
            const type = `${config.feature}:${event}`;
            eventCreators[event as keyof T] = (props: T[typeof event]) => ({ type, props });
        }
    }

    return eventCreators as EventCreators<T>;
};

/**
 * Type representing an actual event
 */
export type EventLog<T extends EventConfig> = {
    [K in keyof T]: { type: string; props: T[K] }
}[keyof T];
